import React, { useEffect, useMemo, useState } from "react";
import { Shield, Eye, Check, X, AlertTriangle, User, Flag, Search, Filter, Clock, Trash2, EyeOff, MessageSquare, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { moderationService } from "../services/moderationService";
import { postsService } from "../services/postsService";

const ModerationView: React.FC = () => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();

  const [activeTab, setActiveTab] = useState<"posts" | "profiles" | "comments" | "reports">("posts");
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [, setActionLoadingId] = useState<string | null>(null);

  const [posts, setPosts] = useState<Record<string, unknown>[]>([]);
  const [profiles, setProfiles] = useState<Record<string, unknown>[]>([]);
  const [comments, setComments] = useState<Record<string, unknown>[]>([]);
  const [reports, setReports] = useState<Record<string, unknown>[]>([]);

  const isModerator = user?.user_type === "moderator" || user?.user_type === "admin";

  useEffect(() => {
    if (activeTab === "profiles" && !["all", "active", "warned", "suspended", "banned"].includes(statusFilter)) {
      setStatusFilter("all");
    } else if (activeTab === "comments" && !["all", "pending", "approved", "rejected", "hidden"].includes(statusFilter)) {
      setStatusFilter("pending");
    } else if (activeTab === "posts" && !["all", "pending", "approved", "rejected"].includes(statusFilter)) {
      setStatusFilter("pending");
    } else if (activeTab === "reports" && !["all", "pending", "resolved", "rejected"].includes(statusFilter)) {
      setStatusFilter("pending");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    let t: number | undefined;
    if (feedback) t = window.setTimeout(() => setFeedback(null), 3000);
    return () => t && window.clearTimeout(t);
  }, [feedback]);

  useEffect(() => {
    (async () => {
      if (!isModerator) return;
      try {
        setLoading(true);
        if (activeTab === "posts") {
          const status = ["all", "pending", "approved", "rejected"].includes(statusFilter) ? statusFilter : "pending";
          const data = await moderationService.getPosts({ status, searchQuery: searchQuery || undefined });
          setPosts(data as Record<string, unknown>[]);
        } else if (activeTab === "profiles") {
          const status = ["all", "active", "warned", "suspended", "banned"].includes(statusFilter) ? statusFilter : "all";
          const data = await moderationService.getProfiles({ status, searchQuery: searchQuery || undefined });
          setProfiles(data as Record<string, unknown>[]);
        } else if (activeTab === "comments") {
          const status = ["all", "pending", "approved", "rejected", "hidden"].includes(statusFilter) ? statusFilter : "pending";
          const data = await moderationService.getComments({ status, searchQuery: searchQuery || undefined });
          setComments(data as Record<string, unknown>[]);
        } else {
          const status = ["all", "pending", "resolved", "rejected"].includes(statusFilter) ? statusFilter : "pending";
          const data = await moderationService.getReports({ status, startDate: startDate || undefined, endDate: endDate || undefined });
          setReports(data as Record<string, unknown>[]);
        }
      } catch (err: unknown) {
        console.error("Error loading moderation data:", err);
        const message =
          err instanceof Error
            ? err.message
            : select({ es: "No se pudo cargar datos.", en: "Failed to load data." });
        setFeedback({ type: "error", message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter, searchQuery, startDate, endDate, isModerator]);

  const statusOptions = useMemo(() => {
    if (activeTab === "profiles") {
      return [
        { value: "all", label: select({ es: "Todos", en: "All" }) },
        { value: "active", label: select({ es: "Activos", en: "Active" }) },
        { value: "warned", label: select({ es: "Advertidos", en: "Warned" }) },
        { value: "suspended", label: select({ es: "Suspendidos", en: "Suspended" }) },
        { value: "banned", label: select({ es: "Baneados", en: "Banned" }) },
      ];
    }
    if (activeTab === "comments") {
      return [
        { value: "all", label: select({ es: "Todos", en: "All" }) },
        { value: "pending", label: select({ es: "Pendientes", en: "Pending" }) },
        { value: "approved", label: select({ es: "Aprobados", en: "Approved" }) },
        { value: "rejected", label: select({ es: "Rechazados", en: "Rejected" }) },
        { value: "hidden", label: select({ es: "Ocultos", en: "Hidden" }) },
      ];
    }
    if (activeTab === "reports") {
      return [
        { value: "all", label: select({ es: "Todos", en: "All" }) },
        { value: "pending", label: select({ es: "Pendientes", en: "Pending" }) },
        { value: "resolved", label: select({ es: "Resueltos", en: "Resolved" }) },
        { value: "rejected", label: select({ es: "Rechazados", en: "Rejected" }) },
      ];
    }
    return [
      { value: "all", label: select({ es: "Todos", en: "All" }) },
      { value: "pending", label: select({ es: "Pendientes", en: "Pending" }) },
      { value: "approved", label: select({ es: "Aprobados", en: "Approved" }) },
      { value: "rejected", label: select({ es: "Rechazados", en: "Rejected" }) },
    ];
  }, [activeTab, select]);

  const formatDate = (value: string) => new Date(value).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });

  const handleExportReports = async () => {
    try {
      const ids = reports.map((r) => r.id as string);
      if (ids.length === 0) { setFeedback({type:"error", message: select({ es:"No hay reportes para exportar.", en:"No reports to export." })}); return; }
      const actions = await moderationService.getModerationActionsByReportIds(ids) as Record<string, unknown>[];
      const map = new Map<string, Record<string, unknown>[]>();
      actions.forEach(a => {
        const reportId = a.report_id as string;
        const arr = map.get(reportId) || [];
        arr.push(a);
        map.set(reportId, arr);
      });
      const header = ["ticket_number","report_id","status","reported_item_type","reported_item_id","reporter","created_at","updated_at","reason","description","actions_history"];
      const escape = (s: unknown) => {
        if (s === null || s === undefined) return "";
        const t = String(s);
        return /[",\n]/.test(t) ? '"' + t.replace(/"/g,'""') + '"' : t;
      };
      const rows = reports.map((r: Record<string, unknown>) => {
        const acts = (map.get(r.id as string) || []).map((a: Record<string, unknown>) => {
          const stamp = new Date(a.created_at as string).toISOString();
          const actionPart = a.action as string;
          const reasonPart = (a.reason as string) || '';
          const bits = [actionPart, reasonPart].filter(Boolean).join(' | ');
          return `${stamp} | ${bits}`;
        }).join('\n');
        const reporterObj = r.reporter as Record<string, unknown> | undefined;
        const reporter = reporterObj?.username || reporterObj?.full_name || r.reporter_id;
        return [
          r.ticket_number ?? '', r.id, r.status, r.reported_item_type, r.reported_item_id,
          reporter, r.created_at, r.updated_at || '', r.reason || '', r.description || '', acts
        ]
          .map(escape).join(',');
      });
      const csv = [header.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `moderation_reports_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      setFeedback({ type: 'success', message: select({ es: 'Informe exportado.', en: 'Report exported.' }) });
    } catch (err: unknown) {
      console.error(err);
      const message =
        err instanceof Error
          ? err.message
          : select({ es: 'No se pudo exportar.', en: 'Export failed.' });
      setFeedback({ type: 'error', message });
    }
  };

  if (!isModerator) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <Shield size={48} className="mx-auto text-red-300 mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">{select({ es: 'Acceso denegado', en: 'Access denied' })}</h3>
          <p className="text-red-700">{select({ es: 'No tienes permisos para acceder al panel de moderación.', en: 'You do not have permission to access the moderation panel.' })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Shield className="text-red-600" size={24} />{select({ es: 'Panel de moderación', en: 'Moderation panel' })}</h1>
        <p className="text-gray-600 mt-1">{select({ es: 'Gestiona contenido y usuarios de la plataforma', en: 'Manage platform content and users' })}</p>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button onClick={() => setActiveTab('posts')} className={`py-3 px-1 border-b-2 ${activeTab==='posts'?'border-red-500 text-red-600 font-medium':'border-transparent text-gray-500 hover:text-gray-700'}`}>{select({ es: 'Publicaciones', en: 'Posts' })}</button>
          <button onClick={() => setActiveTab('profiles')} className={`py-3 px-1 border-b-2 ${activeTab==='profiles'?'border-red-500 text-red-600 font-medium':'border-transparent text-gray-500 hover:text-gray-700'}`}>{select({ es: 'Perfiles', en: 'Profiles' })}</button>
          <button onClick={() => setActiveTab('comments')} className={`py-3 px-1 border-b-2 ${activeTab==='comments'?'border-red-500 text-red-600 font-medium':'border-transparent text-gray-500 hover:text-gray-700'}`}>{select({ es: 'Comentarios', en: 'Comments' })}</button>
          <button onClick={() => setActiveTab('reports')} className={`py-3 px-1 border-b-2 ${activeTab==='reports'?'border-red-500 text-red-600 font-medium':'border-transparent text-gray-500 hover:text-gray-700'}`}>{select({ es: 'Reportes', en: 'Reports' })}</button>
        </nav>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input value={searchQuery} onChange={(e)=>setSearchQuery(e.target.value)} placeholder={select({ es:'Buscar...', en:'Search...' })} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-500" />
          <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
            {statusOptions.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        {activeTab==='reports' && (
          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-4 border-t border-gray-200 pt-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700">{select({ es:'Fecha de inicio', en:'Start date' })}</label>
              <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{select({ es:'Fecha de fin', en:'End date' })}</label>
              <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" />
            </div>
            <div className="flex items-end">
              <button onClick={()=>{/* filtros ya aplicados via state */}} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"><Filter size={16} />{select({ es:'Filtrar Reportes', en:'Filter Reports' })}</button>
            </div>
            <div className="flex items-end">
              <button onClick={handleExportReports} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"><FileText size={16} />{select({ es:'Exportar Informe', en:'Export Report' })}</button>
            </div>
          </div>
        )}
      </div>

      {feedback && (
        <div className={`mb-4 px-4 py-3 rounded-lg border text-sm ${feedback.type==='error'?'bg-red-50 border-red-200 text-red-700':'bg-green-50 border-green-200 text-green-700'}`}>{feedback.message}</div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-4">{[1,2,3].map(i => (<div key={i} className="bg-gray-200 h-32 rounded-xl"></div>))}</div>
      ) : (
        <>
          {/* Posts */}
          {activeTab==='posts' && (
            <div className="space-y-4">
              {posts.map((post: Record<string, unknown>)=> {
                const postProfiles = (post.profiles ?? {}) as Record<string, unknown>;
                return (
                <div key={post.id as string} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{post.title as string}</h3>
                      <p className="text-sm text-gray-500">@{postProfiles.username as string} • {formatDate(post.created_at as string)}</p>
                      <p className="text-gray-700 mt-2 line-clamp-2">{post.description as string}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{(post.moderation_status as string) || 'pending'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <button onClick={async()=>{ setActionLoadingId(post.id as string); await moderationService.moderatePost({ postId: post.id as string, action: 'approved' }); setActionLoadingId(null); setFeedback({type:'success', message: select({ es:'Publicación aprobada.', en:'Post approved.' })}); }} className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs"><Check size={16} /></button>
                    <button onClick={async()=>{ const reason=window.prompt(select({ es:'Motivo del rechazo', en:'Reason for rejection' }), '')||''; if(!reason.trim()) return; setActionLoadingId(post.id as string); await moderationService.moderatePost({ postId: post.id as string, action: 'rejected', reason }); setActionLoadingId(null); setFeedback({type:'success', message: select({ es:'Publicación rechazada.', en:'Post rejected.' })}); }} className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs"><X size={16} /></button>
                    <button onClick={async()=>{ setActionLoadingId(post.id as string); await moderationService.moderatePost({ postId: post.id as string, action: 'pending' }); setActionLoadingId(null); setFeedback({type:'success', message: select({ es:'Publicación marcada como pendiente.', en:'Post marked as pending.' })}); }} className="px-3 py-1.5 bg-yellow-500 text-white rounded-md text-xs"><Clock size={16} /></button>
                    <button onClick={()=>window.open(`/post/${post.id}`, '_blank')} className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs"><Eye size={16} /></button>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* Profiles */}
          {activeTab==='profiles' && (
            <div className="space-y-4">
              {profiles.map((p: Record<string, unknown>)=> (
                <div key={p.id as string} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{p.full_name as string}</h3>
                      <p className="text-sm text-gray-500">@{p.username as string} • {formatDate(p.created_at as string)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{(p.account_status as string) || 'active'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <button onClick={async()=>{ const reason=window.prompt(select({ es:'Motivo de la acción', en:'Reason for this action' }), '')||''; if(!reason.trim()) return; await moderationService.moderateProfile({ profileId: p.id as string, status: 'warned', reason }); setFeedback({type:'success', message: select({ es:'Perfil advertido.', en:'Profile warned.' })}); }} className="px-3 py-1.5 bg-yellow-600 text-white rounded-md text-xs"><AlertTriangle size={16} /></button>
                    <button onClick={async()=>{ const reason=window.prompt(select({ es:'Motivo de la acción', en:'Reason for this action' }), '')||''; if(!reason.trim()) return; await moderationService.moderateProfile({ profileId: p.id as string, status: 'suspended', reason }); setFeedback({type:'success', message: select({ es:'Perfil suspendido.', en:'Profile suspended.' })}); }} className="px-3 py-1.5 bg-orange-600 text-white rounded-md text-xs"><X size={16} /></button>
                    <button onClick={async()=>{ const reason=window.prompt(select({ es:'Motivo de la acción', en:'Reason for this action' }), '')||''; if(!reason.trim()) return; await moderationService.moderateProfile({ profileId: p.id as string, status: 'banned', reason }); setFeedback({type:'success', message: select({ es:'Perfil baneado.', en:'Profile banned.' })}); }} className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs"><Shield size={16} /></button>
                    {(p.account_status as string)!=='active' && (
                      <button onClick={async()=>{ await moderationService.moderateProfile({ profileId: p.id as string, status: 'active' }); setFeedback({type:'success', message: select({ es:'Perfil reactivado.', en:'Profile reactivated.' })}); }} className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs"><Check size={16} /></button>
                    )}
                    <button onClick={()=>window.open(`/profile/${p.username}`, '_blank')} className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs"><User size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Comments */}
          {activeTab==='comments' && (
            <div className="space-y-4">
              {comments.map((c: Record<string, unknown>)=> {
                const commentProfiles = (c.profiles ?? {}) as Record<string, unknown>;
                const commentPosts = (c.posts ?? {}) as Record<string, unknown>;
                return (
                <div key={c.id as string} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2"><MessageSquare size={18} className="text-gray-500" />{select({ es:'Comentario', en:'Comment' })}</h3>
                      <p className="text-sm text-gray-500">@{commentProfiles.username as string} • {formatDate(c.created_at as string)} {commentPosts.title && <>• {select({ es:'Post:', en:'Post:' })} {commentPosts.title as string}</>}</p>
                      <p className="text-gray-700 mt-2">{c.content as string}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{(c.moderation_status as string) || 'pending'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <button onClick={async()=>{ await moderationService.moderateComment({ commentId: c.id as string, action: 'approved' }); setFeedback({type:'success', message: select({ es:'Comentario aprobado.', en:'Comment approved.' })}); }} className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs"><Check size={16} /></button>
                    <button onClick={async()=>{ const reason=window.prompt(select({ es:'Motivo del rechazo', en:'Reason for rejection' }), '')||''; if(!reason.trim()) return; await moderationService.moderateComment({ commentId: c.id as string, action: 'rejected', reason }); setFeedback({type:'success', message: select({ es:'Comentario rechazado.', en:'Comment rejected.' })}); }} className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs"><X size={16} /></button>
                    <button onClick={async()=>{ await moderationService.moderateComment({ commentId: c.id as string, action: 'pending' }); setFeedback({type:'success', message: select({ es:'Comentario pendiente.', en:'Comment pending.' })}); }} className="px-3 py-1.5 bg-yellow-500 text-white rounded-md text-xs"><Clock size={16} /></button>
                    <button onClick={async()=>{ await postsService.updateCommentStatus(c.id as string, 'hidden'); setFeedback({type:'success', message: select({ es:'Comentario ocultado.', en:'Comment hidden.' })}); }} className="px-3 py-1.5 bg-gray-600 text-white rounded-md text-xs"><EyeOff size={16} /></button>
                    <button onClick={async()=>{ if(!window.confirm(select({ es:'¿Eliminar comentario?', en:'Delete comment?' }))) return; await postsService.deleteComment(c.id as string); setFeedback({type:'success', message: select({ es:'Comentario eliminado.', en:'Comment deleted.' })}); }} className="px-3 py-1.5 bg-red-700 text-white rounded-md text-xs"><Trash2 size={16} /></button>
                    {commentPosts.id && (<button onClick={()=>window.open(`/post/${commentPosts.id}`, '_blank')} className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs"><Eye size={16} /></button>)}
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* Reports */}
          {activeTab==='reports' && (
            <div className="space-y-4">
              {reports.map((r: Record<string, unknown>)=> {
                const reporter = (r.reporter ?? {}) as Record<string, unknown>;
                const reportedProfile = (r.reported_profile ?? {}) as Record<string, unknown>;
                const rId = r.id as string;
                const reportedItemType = r.reported_item_type as string;
                const reportedItemId = r.reported_item_id as string;
                return (
                <div key={rId} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 text-sm rounded-full font-medium bg-gray-100 text-gray-700">Ticket #{(r.ticket_number as string) || rId.substring(0,8)}</span>
                        <h3 className="font-semibold text-gray-900 text-lg">{select({ es:'Reporte', en:'Report' })}</h3>
                      </div>
                      <p className="text-sm text-gray-500">{select({ es:'Creado:', en:'Created:' })} {formatDate(r.created_at as string)}</p>
                      <p className="text-xs text-gray-400 mt-1">{select({ es:'Reportado por:', en:'Reported by:' })} {(reporter.full_name as string) || (reporter.username as string) || (r.reporter_id as string)}</p>
                      <p className="text-gray-700 mt-3">{r.reason as string} {r.description && <>— {r.description as string}</>}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{r.status as string}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <button onClick={async()=>{ await moderationService.updateReportStatus(rId, 'resolved'); setFeedback({type:'success', message: select({ es:'Reporte resuelto.', en:'Report resolved.' })}); }} className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs"><Check size={16} /></button>
                    <button onClick={async()=>{ await moderationService.updateReportStatus(rId, 'rejected'); setFeedback({type:'success', message: select({ es:'Reporte descartado.', en:'Report dismissed.' })}); }} className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs"><X size={16} /></button>
                    <button onClick={()=>{
                      if (reportedItemType==='post') window.open(`/post/${reportedItemId}`,'_blank');
                      else if (reportedItemType==='profile') window.open(`/profile/${(reportedProfile.username as string) || reportedItemId}`,'_blank');
                    }} className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs"><Flag size={16} /></button>
                    <button onClick={()=> window.open(`/reports/${rId}`, '_blank')} className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs">{select({ es: 'Ver detalle', en: 'View detail' })}</button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ModerationView;
